/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { Toaster } from 'app/shared/lib/toaster';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { PasswordService } from 'app/core/services/password-service';
import { validate } from 'app/shared/elements/ess-rule';
import { compareObjects } from 'app/shared/lib/compare-objects';
import { deepClone } from 'app/shared/lib/utilities';
import { UserService } from 'app/core/services/user-service';
import { UserCustomerServiceCenters } from 'app/core/components/users/user-customer-service-centers';
import { UserMemberFilters } from 'app/core/components/users/user-member-filters';
import { UserReloadEvent } from 'app/core/lib/events';
import { UserTwoFactorAuthentication } from 'app/core/components/users/user-two-factor-authentication';
import { I18N } from 'aurelia-i18n';
import { IApiSettings } from 'app/shared/lib/api/api-types';
import { ISignOnProviderSettings } from 'app/core/components/users/save-oauth-user';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class SaveUserDetails extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object()
    protected user: Core.User | null = null;
    protected editedUser: Core.User | null = null;
    @bindable.string({ defaultBindingMode: bindingMode.twoWay })
    protected password: string = "";
    protected confirmPassword: string = "";
    protected validateForm = validate;
    protected selectedFilters: Core.Filter[] = [];
    protected allowUserChoiceIsEnabled: boolean = false;
    protected twoFactorAuthenticationIsEnabled: boolean = false;
    protected primaryPhoneNumber: Core.UserPhoneNumber = {};
    protected cellPhoneNumber: Core.UserPhoneNumber = {};
    protected saveLogin: Core.Login = {};
    protected saveFilters: Core.Filter[] = []
    protected saveLocations: Core.Location[] = [];
    protected usesCallCenters: boolean = false;
    protected syncedFields: string[] = [];

    ////****************** Element References ***************************************////

    protected $userCustomerServiceCenters!: UserCustomerServiceCenters;
    protected $userMemberFilters!: UserMemberFilters;
    protected $userTwoFactorAuthentication!: UserTwoFactorAuthentication;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly events: EventAggregator,
        protected readonly passwordService: PasswordService,
        private readonly toaster: Toaster,
        private readonly userService: UserService,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(this.user) {
            this.editedUser = deepClone(this.user);
        }
        this.loadUserSettings();
        this.callCenters();
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////

    public reset() {
        if(this.user) {
            this.editedUser = deepClone(this.user);
        }
        this.password = "";
        this.confirmPassword = "";
        this.loadSyncedFields();
    }

    protected buildUser(): Core.User {
        this.buildPhoneNumbers();
        this.buildLogin();
        this.buildUserFilters();
        if(this.usesCallCenters && this.$userCustomerServiceCenters && this.$userCustomerServiceCenters.locations?.length > 0) { this.buildLocations(); }
        const isSignOnProvider = this.user?.isSignOnProvider;

        let saveUser = {
            id: this.editedUser?.id,
            code: this.editedUser?.code,
            name: {
                first: this.editedUser?.name?.first,
                last: this.editedUser?.name?.last
            },
            title: this.editedUser?.title,
            department: this.editedUser?.department,
            emailAddress: this.editedUser?.emailAddress,
            // Users with the Sign On Provider Account type will be unable to update the TFA Opt-in Flag
            twoFactorAuthentication: isSignOnProvider ? undefined : this.editedUser?.twoFactorAuthentication,
            phoneNumber: {
                primary: this.primaryPhoneNumber || null,
                cell: this.cellPhoneNumber || null
            },
            // Users with the Sign On Provider Account type will be unable to update password info
            login: isSignOnProvider ? undefined : this.saveLogin,
            filters: this.saveFilters,
            locations: this.saveLocations
        };

        return saveUser;
    }

    protected buildLogin(): void {
        if(this.password) {
            this.saveLogin = {
                password: this.password
            }
        }
    }

    protected buildPhoneNumbers(): void {
        if(this.editedUser?.phoneNumber?.primary?.number) {
            this.primaryPhoneNumber = {
                id: this.editedUser?.phoneNumber?.primary?.id,
                number: this.editedUser?.phoneNumber?.primary?.number,
                extension: this.editedUser?.phoneNumber?.primary?.extension
            }
        }
        else if(this.editedUser?.phoneNumber?.primary?.id) {
            this.primaryPhoneNumber = {
                id: this.editedUser?.phoneNumber?.primary?.id
            }
        }

        if(this.editedUser?.phoneNumber?.cell?.number) {
            this.cellPhoneNumber = {
                id: this.editedUser?.phoneNumber?.cell?.id,
                number: this.editedUser?.phoneNumber?.cell?.number,
                extension: this.editedUser?.phoneNumber?.cell?.extension
            }
        }
        else if(this.editedUser?.phoneNumber?.cell?.id) {
            this.cellPhoneNumber = {
                id: this.editedUser?.phoneNumber?.cell?.id
            }
        }
    }

    protected buildUserFilters(): void {
        this.saveFilters = this.$userMemberFilters.selectedFilters.map(filter => ({
            id: filter.id,
            description: filter.description
        }));
    }

    protected buildLocations(): void {
        this.saveLocations = this.$userCustomerServiceCenters.selectedLocations.map(location => ({
            id: location.id,
            description: location.description
        }));
    }

    /**
     * Load the selected sign-on provider's settings to determine which user fields will be populated
     * by the SSO provider's claim data.
     */
    private async loadSyncedFields(): Promise<void> {
        const signOnProviderId = this.user?.login?.signOnProvider?.id;
        if(!!signOnProviderId) {
            const signOnProviderSettings: ISignOnProviderSettings = await this.loadSignOnProviderSettings(signOnProviderId);
            this.syncedFields = signOnProviderSettings?.$claimMappings?.map(claim => claim.path) ?? [];
        }
    }

    /**
     * Gets the synced field message if the specified user field is synced by the SSO provider.
     * @param fieldPath the path of the user field to check
     * @returns the synced field message or null if the field is not synced
     */
    private getSyncedFieldMessage(fieldPath: string): string | null {
        return this.syncedFields.includes(fieldPath) ? this.i18n.tr('ui:messages.info.ssoSyncedFields') : null;
    }

    ////****************** IForm ****************************************************////
    public submittedChanges?: any;

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        const isSuccess = await this.saveUser();
        return isSuccess;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadUserSettings(): Promise<void> {
        const response = await this.userService.loadUserSettings();
        this.twoFactorAuthenticationIsEnabled = response.settings.twoFactorAuthentication.$isEnabled;
        this.allowUserChoiceIsEnabled = response.settings.twoFactorAuthentication.allowUserChoice.$isEnabled;
    }

    @apiCall
    private async callCenters(): Promise<void> {
        const response = await this.userService.loadLocations();
        this.usesCallCenters = response.results.length > 0 ? true : false;
    }

    @apiCall
    private async saveUser(): Promise<boolean> {
        const saveUser = this.buildUser();
        if(!this.user || compareObjects(this.user, saveUser, { returnDifferences: false }).areEqual) return false;
        let isSuccessful = false;
        let errorMessage: string = '';
        try {
            this.state.beginSave();
            const response = await this.userService.saveUser(saveUser);
            isSuccessful = response.isSuccessful;
            if(isSuccessful) { this.events.publish(new UserReloadEvent()); }
        }
        catch(error) {
            if(error.description) { errorMessage = error.description; }
            throw error;
        }
        finally {
            this.toaster.standard.updated('core:user.detail.$', isSuccessful);
            if(!isSuccessful) {
                const exception = (this.toaster.toast())
                    .title('Detail')
                    .class('ess-toast-error')
                    .icon('ui:icons.error')
                    .message(errorMessage)
                exception.show();
            }
            this.reset();
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    private async loadSignOnProviderSettings(signOnProviderId: string): Promise<IApiSettings> {
        try {
            this.state.beginLoad();

            const response = await this.userService.loadSignOnProviderSettings(signOnProviderId);
            return response.settings;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}

